import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { isPathEnabled } from '@/lib/launchGating'
import {
  LayoutDashboard,
  Hammer,
  FolderOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  X,
  Coins,
  ScrollText,
  Brain,
  Megaphone,
  Target,
  CalendarDays,
  Users,
  CreditCard,
  Bell,
  Wrench,
  Plug,
  BarChart3,
  Workflow,
  FlaskConical,
  HelpCircle,
  Shield,
  Cpu,
  Bot,
  Rocket,
  Activity,
  Database,
  CheckCircle,
  DollarSign,
  Search,
  BookOpen,
  Clock,
  MapPin,
  Image,
  ListTodo,
  Crosshair,
  Scale,
  Globe,
  Webhook,
  FileText,
  Sliders,
  Gift,
  Store,
  Sparkles,
  ShieldCheck,
  KeyRound,
  Lock,
  Gauge,
  Building2,
  GitBranch,
  BrainCircuit,
  SlidersHorizontal,
  MessageSquare,
  AlertTriangle,
  HeartPulse,
  GraduationCap,
  Zap,
  Trophy,
  LayoutGrid,
  Film,
  Layers,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { signOut } from '@/lib/auth'
import { sidebarSpring } from '@/styles/theme'
import { OrgSwitcher } from '@/components/workspace/OrgSwitcher'
import { WorkspaceSwitcher } from '@/components/workspace/WorkspaceSwitcher'

interface SidebarProps {
  isCollapsed: boolean
  toggleSidebar: () => void
  isMobile: boolean
  isMobileOpen: boolean
  closeMobileSidebar: () => void
}

interface NavItem {
  icon: React.ElementType
  label: string
  path: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const primaryItems: NavItem[] = [
  { icon: Hammer, label: 'Build', path: '/build' },
  { icon: LayoutGrid, label: 'Templates', path: '/templates' },
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: FolderOpen, label: 'Projects', path: '/projects' },
  { icon: Rocket, label: 'Deployments', path: '/deployments' },
  { icon: Store, label: 'Marketplace', path: '/marketplace' },
  { icon: Trophy, label: 'Showcase', path: '/showcase' },
]

// Anonymous visitors get a stripped-down nav. Showing the full
// LAUNCH/SCHEDULE/INSIGHTS/etc surface to someone who isn't signed in
// is misleading — every privileged route 401s and the visual implies
// the user is logged in when they aren't.
const anonPrimaryItems: NavItem[] = [
  { icon: Hammer, label: 'Build', path: '/build' },
  { icon: LayoutGrid, label: 'Templates', path: '/templates' },
  { icon: Trophy, label: 'Showcase', path: '/showcase' },
]

const createGroup: NavGroup = {
  label: 'Create',
  items: [
    { icon: Sparkles, label: 'Content Studio', path: '/content-studio' },
    { icon: FileText, label: 'Blog', path: '/content-studio/blog' },
    { icon: Film, label: 'Video', path: '/content-studio/video' },
    { icon: Image, label: 'Ad Creatives', path: '/content-studio/ads' },
    { icon: Layers, label: 'Bulk Generation', path: '/content-studio/bulk' },
    { icon: Bot, label: 'Avatar Studio', path: '/content-studio/avatar' },
    { icon: Globe, label: 'Brand Voice', path: '/content-studio/brand-voice/languages' },
    { icon: BookOpen, label: 'Content Templates', path: '/content-studio/templates' },
  ],
}

const launchGroup: NavGroup = {
  label: 'Launch',
  items: [
    { icon: Target, label: 'Campaigns', path: '/marketing/campaigns' },
    { icon: FlaskConical, label: 'A/B Tests', path: '/marketing/ab-tests' },
    { icon: Workflow, label: 'Nurture', path: '/marketing/nurture' },
    { icon: Gift, label: 'Referrals', path: '/marketing/referrals' },
    { icon: Users, label: 'Leads', path: '/marketing/leads' },
    { icon: Search, label: 'SEO', path: '/marketing/seo' },
    { icon: Megaphone, label: 'App Marketing', path: '/app-marketing' },
    { icon: Store, label: 'Providers', path: '/marketing/providers' },
  ],
}

const scheduleGroup: NavGroup = {
  label: 'Schedule',
  items: [
    { icon: CalendarDays, label: 'Calendar', path: '/marketing/calendar' },
    { icon: Clock, label: 'Scheduler', path: '/marketing/scheduler' },
    { icon: CheckCircle, label: 'Approvals', path: '/approvals' },
    { icon: ListTodo, label: 'Tasks', path: '/marketing/tasks' },
  ],
}

const insightsGroup: NavGroup = {
  label: 'Insights',
  items: [
    { icon: BarChart3, label: 'Overview', path: '/marketing' },
    { icon: Target, label: 'Campaign Analytics', path: '/marketing/analytics' },
    { icon: Activity, label: 'Content Analytics', path: '/content-studio/analytics' },
    { icon: FileText, label: 'Reports', path: '/marketing/reports' },
    { icon: Crosshair, label: 'Competitive', path: '/content-studio/competitive' },
    { icon: DollarSign, label: 'Tracking', path: '/marketing/tracking' },
    { icon: Scale, label: 'Supply & Demand', path: '/marketing/supply-demand' },
    { icon: MapPin, label: 'Geography', path: '/marketing/geography' },
  ],
}

const assetsGroup: NavGroup = {
  label: 'Assets',
  items: [
    { icon: Image, label: 'Asset Library', path: '/marketing/assets' },
    { icon: Database, label: 'CMS', path: '/content-studio/cms' },
    { icon: Brain, label: 'Knowledge', path: '/knowledge' },
  ],
}

const configureGroup: NavGroup = {
  label: 'Configure',
  items: [
    { icon: Plug, label: 'Connectors', path: '/connectors' },
    { icon: Coins, label: 'Token Usage', path: '/token-usage' },
    { icon: Shield, label: 'Rules', path: '/rules' },
    { icon: Cpu, label: 'Models', path: '/model-settings' },
    { icon: Sliders, label: 'Config', path: '/marketing/config' },
    { icon: Webhook, label: 'Webhooks', path: '/marketing/webhooks' },
  ],
}

const operationsGroup: NavGroup = {
  label: 'Operations',
  items: [
    { icon: Activity, label: 'Monitoring', path: '/monitoring' },
    { icon: Search, label: 'Traces', path: '/traces' },
    { icon: CheckCircle, label: 'Approvals', path: '/approvals' },
    { icon: DollarSign, label: 'Costs', path: '/costs' },
  ],
}

const aiIntelligenceGroup: NavGroup = {
  label: 'Intelligence',
  items: [
    { icon: BrainCircuit, label: 'Brain', path: '/brain' },
    { icon: GitBranch, label: 'Knowledge Graph', path: '/knowledge-graph' },
    { icon: SlidersHorizontal, label: 'Context Budget', path: '/context-budget' },
    { icon: MessageSquare, label: 'Coordination', path: '/agent-coordination' },
    { icon: AlertTriangle, label: 'Error Prediction', path: '/error-prediction' },
    { icon: HeartPulse, label: 'Self-Healing', path: '/self-healing' },
    { icon: GraduationCap, label: 'Corrections', path: '/correction-learning' },
    { icon: Zap, label: 'Autonomous Dev', path: '/autonomous-dev' },
  ],
}

const platformAdminGroup: NavGroup = {
  label: 'Platform Admin',
  items: [
    { icon: Wrench, label: 'LLM Providers', path: '/admin/providers' },
    { icon: Cpu, label: 'Catalog', path: '/admin/catalog' },
    { icon: Bot, label: 'Agents', path: '/admin/agents' },
    { icon: Settings, label: 'System', path: '/admin/system' },
    { icon: BookOpen, label: 'Ops', path: '/admin/ops' },
  ],
}

const enterpriseGroup: NavGroup = {
  label: 'Enterprise',
  items: [
    { icon: ShieldCheck, label: 'Compliance', path: '/compliance' },
    { icon: KeyRound, label: 'RBAC', path: '/rbac-settings' },
    { icon: Lock, label: 'SSO & SCIM', path: '/sso-settings' },
    { icon: Gauge, label: 'SLA', path: '/sla' },
    { icon: Building2, label: 'Status Page', path: '/status' },
  ],
}


const adminObservabilityGroup: NavGroup = {
  label: 'Observability',
  items: [
    { icon: Activity, label: 'Monitoring', path: '/monitoring' },
    { icon: Search, label: 'Traces', path: '/traces' },
    { icon: ScrollText, label: 'Audit Log', path: '/audit-log' },
  ],
}

const adminAccountItems: NavItem[] = [
  { icon: Settings, label: 'Settings', path: '/settings' },
]

const accountItems: NavItem[] = [
  { icon: Users, label: 'Team', path: '/team' },
  { icon: CreditCard, label: 'Billing', path: '/billing' },
  { icon: Bell, label: 'Notifications', path: '/notifications' },
  { icon: Trophy, label: 'Referrals', path: '/referral-program' },
  { icon: Settings, label: 'Settings', path: '/settings' },
  { icon: Wrench, label: 'Developer Portal', path: '/developers' },
  { icon: Database, label: 'Backups', path: '/backups' },
  { icon: FileText, label: 'Changelog', path: '/changelog' },
  { icon: HelpCircle, label: 'Help', path: '/help' },
]

function NavLink({
  item,
  isActive,
  isCollapsed,
  isMobile,
  onClick,
}: {
  item: NavItem
  isActive: boolean
  isCollapsed: boolean
  isMobile: boolean
  onClick?: () => void
}) {
  return (
    <Link
      to={item.path}
      onClick={onClick}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center px-3 py-2.5 rounded-xl transition-all duration-200 group relative focus-visible:outline-2 focus-visible:outline-lime focus-visible:outline-offset-2',
        isActive
          ? 'bg-white/10 text-lime'
          : 'text-gray-400 hover:bg-white/5 hover:text-white'
      )}
    >
      <item.icon className={cn('w-5 h-5 shrink-0', isCollapsed && !isMobile ? 'mx-auto' : 'mr-3')} aria-hidden="true" />
      {(!isCollapsed || isMobile) && <span className="text-sm font-medium">{item.label}</span>}
      {isActive && (
        <motion.div
          layoutId="activeSidebar"
          className="absolute left-0 w-1 h-6 bg-lime rounded-r-full"
        />
      )}
    </Link>
  )
}

function CollapsibleGroup({
  group,
  isCollapsed,
  isMobile,
  currentPath,
  onItemClick,
  defaultOpen,
}: {
  group: NavGroup
  isCollapsed: boolean
  isMobile: boolean
  currentPath: string
  onItemClick?: () => void
  defaultOpen?: boolean
}) {
  // Launch gating (Plan 33 / RR-5): hide FLAG_OFF nav items; drop the whole
  // group when nothing launch-ready remains. SHIP items pass through unchanged.
  const items = group.items.filter((item) => isPathEnabled(item.path))
  const isAnyActive = items.some((item) => currentPath === item.path || currentPath.startsWith(item.path + '/'))
  const [isOpen, setIsOpen] = useState(defaultOpen ?? isAnyActive)

  if (items.length === 0) return null

  if (isCollapsed && !isMobile) {
    return (
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            isActive={currentPath === item.path}
            isCollapsed={isCollapsed}
            isMobile={isMobile}
            onClick={onItemClick}
          />
        ))}
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={`${group.label} navigation group`}
        className="flex items-center w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-300 transition-colors focus-visible:outline-2 focus-visible:outline-lime"
      >
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden pl-1 space-y-0.5"
          >
            {items.map((item) => (
              <NavLink
                key={item.path}
                item={item}
                isActive={currentPath === item.path}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                onClick={onItemClick}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function Sidebar({ isCollapsed, toggleSidebar, isMobile, isMobileOpen, closeMobileSidebar }: SidebarProps) {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin)

  const handleSignOut = async () => {
    useAuthStore.getState().logout()
    await signOut()
  }

  const onItemClick = isMobile ? closeMobileSidebar : undefined

  const sidebarContent = (
    <nav className="flex flex-col h-full" role="navigation" aria-label="Main navigation">
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-6 border-b border-white/10 shrink-0">
        {!isCollapsed && (
          <div className="flex flex-col">
            <Link to={isPlatformAdmin ? "/admin/providers" : "/build"} className="text-xl font-bold bg-linear-to-r from-lime to-cyan bg-clip-text text-transparent leading-tight">
              Qyngent
            </Link>
            <span className="text-[9px] text-yellow-400/70 font-medium tracking-wider uppercase leading-none mt-0.5">Beta — features may be unstable</span>
          </div>
        )}
        <button
          onClick={isMobile ? closeMobileSidebar : toggleSidebar}
          aria-label={isMobile ? 'Close sidebar' : isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors ml-auto focus-visible:outline-2 focus-visible:outline-lime"
        >
          {isMobile ? <X className="w-5 h-5" aria-hidden="true" /> : isCollapsed ? <ChevronRight className="w-5 h-5" aria-hidden="true" /> : <ChevronLeft className="w-5 h-5" aria-hidden="true" />}
        </button>
      </div>

      {/* Skip to main content link (only visible on focus for keyboard users) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-20 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-lime focus:text-black focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>

      {/* Tenant scope, coarse to fine. OrgSwitcher renders itself away
          unless the user belongs to more than one organisation. */}
      <OrgSwitcher isCollapsed={isCollapsed} />
      <WorkspaceSwitcher isCollapsed={isCollapsed} />

      {/* Nav items */}
      <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto custom-scrollbar">
        {isPlatformAdmin ? (
          <>
            {/* Platform Admin — primary group, expanded by default */}
            <div className="space-y-0.5 mb-4">
              <CollapsibleGroup
                group={platformAdminGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
                defaultOpen
              />
            </div>

            {/* Observability */}
            <div className="mb-2">
              <CollapsibleGroup
                group={adminObservabilityGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Enterprise */}
            <div className="mb-2">
              <CollapsibleGroup
                group={enterpriseGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Operations */}
            <div className="mb-2">
              <CollapsibleGroup
                group={operationsGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* AI Intelligence */}
            <div className="mb-2">
              <CollapsibleGroup
                group={aiIntelligenceGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Divider */}
            <div className="h-px bg-white/10 my-3" />

            {/* Account — minimal for admin */}
            <div className="space-y-0.5">
              {(!isCollapsed || isMobile) && (
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Account</div>
              )}
              {adminAccountItems.filter((item) => isPathEnabled(item.path)).map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  isCollapsed={isCollapsed}
                  isMobile={isMobile}
                  onClick={onItemClick}
                />
              ))}
            </div>
          </>
        ) : !user ? (
          // ── Anonymous visitor: minimal public nav (Plan 32 #117). ──
          // Don't render privileged groups (LAUNCH/SCHEDULE/INSIGHTS/...)
          // for someone who isn't signed in — every link would 401 and
          // the rendered chrome implies "you're inside the app" when
          // you're not. CTA at the bottom directs them to sign up.
          <>
            <div className="space-y-0.5 mb-4">
              {anonPrimaryItems.filter((item) => isPathEnabled(item.path)).map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  isCollapsed={isCollapsed}
                  isMobile={isMobile}
                  onClick={onItemClick}
                />
              ))}
            </div>
            {(!isCollapsed || isMobile) && (
              <div className="mt-auto px-3 pb-2 pt-6 space-y-2">
                <Link
                  to="/login"
                  className="block w-full text-center rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-gray-200 hover:bg-white/10 transition-colors"
                  onClick={onItemClick}
                >
                  Sign in
                </Link>
                <Link
                  to="/login?signup=1"
                  className="block w-full text-center rounded-lg bg-lime px-3 py-2 text-sm font-semibold text-black hover:bg-lime/90 transition-colors"
                  onClick={onItemClick}
                  data-testid="sidebar-anon-signup-cta"
                >
                  Sign up free
                </Link>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Primary */}
            <div className="space-y-0.5 mb-4">
              {primaryItems.filter((item) => isPathEnabled(item.path)).map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  isCollapsed={isCollapsed}
                  isMobile={isMobile}
                  onClick={onItemClick}
                />
              ))}
            </div>

            {/* Create */}
            <div className="mb-2">
              <CollapsibleGroup
                group={createGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Launch */}
            <div className="mb-2">
              <CollapsibleGroup
                group={launchGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Schedule */}
            <div className="mb-2">
              <CollapsibleGroup
                group={scheduleGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Insights */}
            <div className="mb-2">
              <CollapsibleGroup
                group={insightsGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Assets */}
            <div className="mb-2">
              <CollapsibleGroup
                group={assetsGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Configure */}
            <div className="mb-2">
              <CollapsibleGroup
                group={configureGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Operations */}
            <div className="mb-2">
              <CollapsibleGroup
                group={operationsGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Intelligence */}
            <div className="mb-2">
              <CollapsibleGroup
                group={aiIntelligenceGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Enterprise */}
            <div className="mb-2">
              <CollapsibleGroup
                group={enterpriseGroup}
                isCollapsed={isCollapsed}
                isMobile={isMobile}
                currentPath={location.pathname}
                onItemClick={onItemClick}
              />
            </div>

            {/* Divider */}
            <div className="h-px bg-white/10 my-3" />

            {/* Account */}
            <div className="space-y-0.5">
              {(!isCollapsed || isMobile) && (
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Account</div>
              )}
              {accountItems.filter((item) => isPathEnabled(item.path)).map((item) => (
                <NavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname === item.path}
                  isCollapsed={isCollapsed}
                  isMobile={isMobile}
                  onClick={onItemClick}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {user && (
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleSignOut}
            aria-label="Sign out"
            className={cn(
              'flex items-center w-full px-3 py-3 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors focus-visible:outline-2 focus-visible:outline-red-400',
              isCollapsed && !isMobile && 'justify-center'
            )}
          >
            <LogOut className={cn('w-5 h-5', (!isCollapsed || isMobile) && 'mr-3')} aria-hidden="true" />
            {(!isCollapsed || isMobile) && <span className="font-medium">Sign Out</span>}
          </button>
        </div>
      )}
    </nav>
  )

  if (isMobile) {
    return (
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 z-30"
              onClick={closeMobileSidebar}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={sidebarSpring}
              className="fixed top-0 left-0 h-screen w-[280px] bg-[#0a0a0a] border-r border-white/10 z-40"
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    )
  }

  return (
    <motion.div
      animate={{ width: isCollapsed ? 80 : 280 }}
      transition={sidebarSpring}
      className="h-screen bg-[#0a0a0a] border-r border-white/10 flex-col fixed left-0 top-0 z-40 hidden lg:flex"
    >
      {sidebarContent}
    </motion.div>
  )
}
